import { useEffect, useState, useRef } from 'react';
import { api } from '../api';
import { ErrorState } from '../ui/State';
type Payment = {
  id: string;
  demand_id: string;
  amount: string;
  status: string;
  hosted_url: string | null;
};
export function HostedCardPayments({
  premises,
  demands,
}: {
  premises: string;
  demands: { id: string; reference: string; status: string }[];
}) {
  const [model, setModel] = useState<{ configured: boolean; payments: Payment[] }>(),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false),
    pending = useRef<{ path: string; key: string } | null>(null),
    root = '/api/portal/leaseholder/' + premises + '/payments';
  const load = () => api<{ configured: boolean; payments: Payment[] }>(root).then(setModel);
  useEffect(() => {
    void load().catch(setError);
  }, [premises]);
  async function act(path: string) {
    pending.current ??= { path, key: crypto.randomUUID() };
    setBusy(true);
    try {
      await api(root + pending.current.path, {
        method: 'POST',
        body: JSON.stringify({ idempotencyKey: pending.current.key }),
      });
      pending.current = null;
      await load();
      setError(undefined);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="panel">
      <h2>Card payments</h2>
      {Boolean(error) && <ErrorState error={error} />}
      <p>
        Card payments only. Direct Debit is not accepted. Your balance changes after your manager
        reconciles the bank receipt.
      </p>
      {model && !model.configured && (
        <p>Card payments are not available yet. Use the payment instructions on your demand.</p>
      )}
      {model?.configured && (
        <>
          {demands
            .filter(
              (d) =>
                d.status === 'Issued' &&
                !model.payments.some(
                  (p) => p.demand_id === d.id && !['Expired', 'Creating'].includes(p.status),
                ),
            )
            .map((d) => (
              <p key={d.id}>
                {d.reference}{' '}
                <button
                  disabled={busy || !!pending.current}
                  onClick={() => void act('/' + d.id + '/start')}
                >
                  Prepare card payment
                </button>
              </p>
            ))}
          {model.payments.map((p) => (
            <article key={p.id}>
              <p>
                £{p.amount} ·{' '}
                {p.status === 'Paid'
                  ? 'Payment confirmed — awaiting bank reconciliation'
                  : p.status}
              </p>
              {p.hosted_url && (
                <a href={p.hosted_url} target="_blank" rel="noopener noreferrer">
                  Continue to secure card payment
                </a>
              )}
              <button
                disabled={busy || !!pending.current}
                onClick={() => void act('/' + p.id + '/refresh')}
              >
                Check payment status
              </button>
            </article>
          ))}
          {pending.current && (
            <button disabled={busy} onClick={() => void act(pending.current!.path)}>
              Retry the same payment request
            </button>
          )}
        </>
      )}
    </section>
  );
}
